<!--
	A bootstrap modal component, see https://getbootstrap.com/docs/3.3/javascript/#modals
-->
<template>
	<focus-trap v-model:active="isFocusTrapActive">
		<div
			:id="modalId"
			:modal-id="modalId"
			:data-backdrop="preventBackdropClose ? 'static' : true"
			class="modal modal-centered fade"
			tabindex="-1"
			role="dialog"
		>
			<div v-if="renderModalWhenClosed || shown" v-click-outside="onClickOutside" class="modal-dialog" role="document">
				<div ref="modalContent" class="modal-content">
					<div class="modal-header">
						<a v-if="cross" class="close" href="#" data-dismiss="modal" @click.prevent="$emit('crossClick')">
							<responsive-image :from="cross" :inline-svg="inlineSvgCross" />
						</a>
						<slot name="title" />
					</div>
					<div class="modal-body">
						<slot name="body" />
					</div>
					<div v-if="$slots['footer']" class="modal-footer">
						<slot name="footer" />
					</div>
					<div v-if="$slots['nested-modal']" class="nested-modal">
						<slot name="nested-modal" />
					</div>
				</div>
			</div>
		</div>
	</focus-trap>
</template>
<script lang="ts">
import { Vue, Component, Prop, toNative } from "vue-facing-decorator";
import { MiaPlaza } from "@/Reinforced.Typings";
import ResponsiveImage from "@/Control/Vue/ResponsiveImage";
import IdGenerator from "@/utils/IdGenerator";
import { ClickOutside } from "vuetify/directives";
import { FocusTrap } from "focus-trap-vue";

@Component({
	emits: [
		"clickOutside",
		"crossClick",
		"close",
		"open",
	],
	directives: {
		ClickOutside,
	},
	components: {
		ResponsiveImage,
		FocusTrap,
	},
})
export class Modal extends Vue {
	@Prop({ required: false, default: null }) cross!: MiaPlaza.Navigation.JSON.Converters.ILazySizes;
	@Prop({ required: false, type: String, default: () => IdGenerator.Generate("modal") }) modalId!: string;
	@Prop({ required: false, type: Boolean, default: false }) private initiallyShown!: boolean;
	@Prop({ required: false, type: Boolean, default: true }) inlineSvgCross!: boolean;
	@Prop({ required: false, type: Boolean, default: false }) preventBackdropClose!: boolean;
	@Prop({ required: false, type: Boolean, default: true }) focusTrapEnabled!: boolean;

	/**
	 * This Modal vue component is just a wrapper of Bootstrap modal.
	 * And Bootstrap modal is always added to the page. Even when modal is
	 * closed, the modal content is hidden but rendered into the page.
	 *
	 * Set this to false, when modal content should not be rendered on the page when modal is closed
	 * (this is useful for heavy modals, e.g. modal containing lists like breaks & holidays modal).
	 */
	@Prop({ required: false, type: Boolean, default: true }) renderModalWhenClosed!: boolean;

	/**
	 * This field is updated on show/hide actions of Bootstrap modal.
	 * Is used to not render the content of modal until it is actually opened when renderModalWhenClosed is true.
	 */
	shown = false;

	isFocusTrapActive = false;

	get onClickOutside(): { handler: (e: MouseEvent) => void, include: () => HTMLElement[] } {
		return {
			handler: () => this.$emit("clickOutside"),
			include: () => [...document.querySelectorAll<HTMLElement>(".mia-teleported-dropdown")],
		};
	}

	private get modal(): JQuery<HTMLElement> {
		return jQuery(`#${this.modalId}`);
	}

	private mounted() {
		this.modal.on("hide.bs.modal", () => {
			this.shown = false;
			this.isFocusTrapActive = false;
			this.$emit("close");
		});
		this.modal.on("show.bs.modal", () => {
			this.shown = true;
			this.$emit("open");
		});
		this.modal.on("shown.bs.modal", () => {
			this.isFocusTrapActive = this.focusTrapEnabled;
		});

		if (this.initiallyShown) {
			this.modal.modal("show");
		}
	}

	private beforeUnmount() {
		this.modal.off("hide.bs.modal");
		this.modal.off("show.bs.modal");
		this.modal.off("shown.bs.modal");
		if (this.initiallyShown) {
			this.modal.modal("hide");
		}
	}
}

export default toNative(Modal);
</script>
<style lang="scss">
.mia-Modal {
	.modal-body {
		> *:first-child {
			margin-top: 0;
		}

		> *:last-child {
			margin-bottom: 0;
		}

		> div:only-child {
			> *:first-child {
				margin-top: 0;
			}

			> *:last-child {
				margin-bottom: 0;
			}
		}
	}
}
</style>
